// Invoices list — desktop table and mobile card list, with the Balance-due
// hero, status tabs (Unpaid / Upcoming / Paid / Void) and search across every
// status. Layout follows the "Invoices v3" design handoff.
import { Box, ButtonBase, InputBase, CircularProgress } from '@mui/material'
import { T, card, ghostBtn, overline, StatusPill, InvIcon } from './invoicesUi'

const TAB_DEFS = [
	{ id: 'unpaid', label: 'Unpaid' },
	{ id: 'upcoming', label: 'Upcoming' },
	{ id: 'paid', label: 'Paid' },
	{ id: 'void', label: 'Void' },
]

const CAPTIONS = {
	unpaid: 'Owed longest first',
	upcoming: 'Soonest first · completes at checkout',
	paid: 'Most recent first · each one is a receipt',
	void: 'Most recent first · records are kept',
}

function Tabs({ tab, onTab, totals, isMobile }) {
	const counts = { unpaid: totals.unpaidCount, upcoming: totals.upcomingCount }
	return (
		<Box sx={{ display: 'flex', gap: '3px', p: '4px', bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: '12px', ...(isMobile ? {} : { display: 'inline-flex', alignSelf: 'flex-start' }) }}>
			{TAB_DEFS.map((t) => {
				const on = t.id === tab
				const count = counts[t.id]
				return (
					<ButtonBase key={t.id} onClick={() => onTab(t.id)} sx={{
						flex: isMobile ? 1 : 'none',
						display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
						height: isMobile ? 36 : 34, px: isMobile ? '8px' : '14px', border: 'none', borderRadius: '9px',
						bgcolor: on ? T.field : 'transparent', color: on ? T.text : T.muted,
						fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', fontFamily: T.font,
						boxShadow: on ? '0 1px 3px rgba(46,42,64,0.10)' : 'none',
						'&:hover': { color: T.text },
					}}>
						{t.label}
						{count > 0 && (
							<Box component="span" sx={{
								display: 'inline-flex', alignItems: 'center', height: 20, px: '7px', borderRadius: '99px',
								bgcolor: on ? T.primarySoft : T.border, color: on ? T.primaryDeep : T.soft,
								fontSize: '11px', fontWeight: 750, fontVariantNumeric: 'tabular-nums',
							}}>
								{count}
							</Box>
						)}
					</ButtonBase>
				)
			})}
		</Box>
	)
}

function SearchBox({ query, onQuery, isMobile }) {
	return (
		<Box component="label" sx={{
			display: 'flex', alignItems: 'center', gap: '9px',
			width: isMobile ? '100%' : 300, height: isMobile ? 44 : 38, px: '12px',
			border: `1.5px solid ${T.border}`, borderRadius: isMobile ? '12px' : '10px',
			bgcolor: T.field, color: T.muted,
			'&:focus-within': { borderColor: T.primary },
		}}>
			<InvIcon name="search" size={15} />
			<InputBase
				value={query}
				onChange={(e) => onQuery(e.target.value)}
				placeholder="Search client, pet, invoice # or amount…"
				sx={{
					flex: 1, fontSize: isMobile ? '15px' : '13.5px', fontWeight: 600, color: T.text, fontFamily: T.font,
					'& input::placeholder': { color: '#B9B2CC', fontWeight: 500, opacity: 1 },
				}}
			/>
			{query && (
				<ButtonBase aria-label="Clear search" onClick={() => onQuery('')} sx={{
					width: 26, height: 26, flex: 'none', borderRadius: '50%', bgcolor: T.surface, color: T.soft,
				}}>
					<InvIcon name="x" size={12} sw={2} />
				</ButtonBase>
			)}
		</Box>
	)
}

function EmptyRows({ searching }) {
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: '38px 20px 36px', gap: '6px' }}>
			<Box sx={{ width: 42, height: 42, borderRadius: '50%', bgcolor: T.okBg, color: T.okIcon, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '4px' }}>
				<InvIcon name="tickCircle" size={20} />
			</Box>
			<Box sx={{ fontSize: '14.5px', fontWeight: 750, color: T.text, fontFamily: T.font }}>
				{searching ? 'No matches' : 'Nothing here yet'}
			</Box>
			<Box sx={{ fontSize: '13px', fontWeight: 600, color: T.muted, lineHeight: 1.5, maxWidth: '46ch', fontFamily: T.font }}>
				{searching
					? 'Try a client or pet name, an invoice number, or an amount.'
					: 'Invoices are created with appointments and complete themselves at checkout.'}
			</Box>
		</Box>
	)
}

function FirstRun({ isMobile }) {
	const steps = [
		['Book', 'The invoice appears here as Upcoming, prefilled from the appointment.'],
		['Check out', "Paid on the day? It's marked Paid and doubles as the receipt."],
		['Still owed?', 'It shows as Unpaid, then check out the appointment when they pay — money is always taken at checkout.'],
	]
	return (
		<Box sx={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: isMobile ? '44px 20px 32px' : '60px 24px 48px' }}>
			<Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: T.primarySoft, color: T.primaryDeep, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<InvIcon name="receipt" size={28} sw={1.4} />
			</Box>
			<Box component="h2" sx={{ m: '16px 0 0', fontSize: '18px', fontWeight: 800, letterSpacing: '-0.01em', color: T.text, fontFamily: T.font }}>No invoices yet</Box>
			<Box component="p" sx={{ m: '7px 0 0', maxWidth: '48ch', fontSize: '13px', fontWeight: 600, color: T.muted, lineHeight: 1.55, fontFamily: T.font }}>
				Invoices are created for you the moment you book an appointment, and complete themselves at checkout. There's nothing to set up.
			</Box>
			<Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '12px', mt: '32px', width: '100%', maxWidth: 760 }}>
				{steps.map(([title, body], i) => (
					<Box key={title} sx={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: 'flex-start', gap: isMobile ? '12px' : '4px', textAlign: 'left', border: '1.5px solid #EAE6F3', borderRadius: '13px', bgcolor: T.field, p: '13px 14px' }}>
						<Box sx={{ width: 30, height: 30, flex: 'none', borderRadius: '9px', bgcolor: T.surface, color: T.primaryDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12.5px', fontWeight: 800, mb: isMobile ? 0 : '4px', fontFamily: T.font }}>{i + 1}</Box>
						<Box sx={{ fontFamily: T.font }}>
							<Box component="strong" sx={{ fontSize: '13px', fontWeight: 750, color: T.text }}>{title}</Box>
							<Box sx={{ fontSize: '12px', fontWeight: 600, color: T.muted, lineHeight: 1.45, mt: '2px' }}>{body}</Box>
						</Box>
					</Box>
				))}
			</Box>
		</Box>
	)
}

function LoadError({ onRetry }) {
	return (
		<Box sx={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: '56px 24px 48px' }}>
			<Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: T.warnBg, color: '#C08A2D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<InvIcon name="warn" size={26} sw={1.6} />
			</Box>
			<Box component="h2" sx={{ m: '16px 0 0', fontSize: '17px', fontWeight: 800, color: T.text, fontFamily: T.font }}>Couldn't load your invoices</Box>
			<Box component="p" sx={{ m: '7px 0 0', maxWidth: '44ch', fontSize: '13px', fontWeight: 600, color: T.muted, lineHeight: 1.55, fontFamily: T.font }}>
				Check your connection and try again. Nothing has changed — your invoices are safe.
			</Box>
			<ButtonBase onClick={onRetry} sx={{ mt: '18px', height: 40, px: '16px', borderRadius: '10px', bgcolor: T.primary, color: '#fff', fontSize: '13.5px', fontWeight: 700, fontFamily: T.font, gap: '7px', boxShadow: T.btnShadow, '&:hover': { bgcolor: T.primaryDeep } }}>
				<InvIcon name="retry" size={15} sw={1.8} />Try again
			</ButtonBase>
		</Box>
	)
}

/* ---------- desktop table ---------- */

const GRID = 'minmax(200px, 1fr) 170px 90px 150px 120px 170px'

function DesktopRow({ inv, searching, onOpen, onOpenAppointment, onDownload, downloadingId }) {
	const col4 = searching ? null
		: inv.isUnpaid ? inv.totalFmt
		: inv.isUpcoming ? `${inv.time}${inv.staff ? ` · ${inv.staff}` : ''}`
		: inv.isPaid ? (inv.payments[0] ? `${inv.payments[0].methodLabel} · ${inv.payments[0].dateFmt}` : 'Paid')
		: `Voided ${inv.voidedAtFmt || ''}`
	const col4Money = !searching && inv.isUnpaid
	const apptSub = inv.isUnpaid ? `${inv.rel}${inv.staff ? ` · ${inv.staff}` : ''}`
		: inv.isUpcoming ? `${inv.rel}${inv.staff ? ` · with ${inv.staff}` : ''}`
		: inv.staff || ''
	const strike = inv.isVoid ? { textDecoration: 'line-through', color: '#A9A3BE' } : {}
	return (
		<Box onClick={() => onOpen(inv)} sx={{
			display: 'grid', gridTemplateColumns: GRID, gap: '14px', alignItems: 'center',
			p: '11px 18px', borderTop: `1px solid ${T.rowDivider}`, cursor: 'pointer', fontFamily: T.font,
			'&:hover': { bgcolor: '#FCFBFE' },
		}}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
					<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 700, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...strike }}>{inv.clientName}</Box>
					<Box sx={{ fontSize: '12px', fontWeight: 600, color: T.breed, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.petLine}</Box>
				</Box>
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
				<Box sx={{ fontSize: '13px', fontWeight: 650, color: T.soft, whiteSpace: 'nowrap' }}>
					{inv.isUpcoming ? `${inv.dateFmt} · ${inv.time}` : inv.dateFmt}
				</Box>
				<Box sx={{ fontSize: '11px', fontWeight: inv.isAging ? 700 : 600, color: inv.isAging ? T.warnText : T.faint, whiteSpace: 'nowrap' }}>{apptSub}</Box>
			</Box>
			<Box sx={{ fontSize: '12.5px', fontWeight: 650, color: T.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{inv.number}</Box>
			{searching ? (
				<Box><StatusPill status={inv.status} /></Box>
			) : (
				<Box sx={{ fontSize: col4Money ? '13px' : '12.5px', fontWeight: 650, color: T.soft, textAlign: col4Money ? 'right' : 'left', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{col4}</Box>
			)}
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px', textAlign: 'right' }}>
				<Box sx={{
					fontSize: inv.isUnpaid ? '14px' : '13.5px', fontWeight: inv.isUnpaid ? 800 : 700,
					color: inv.isVoid ? '#A9A3BE' : inv.isUpcoming ? T.soft : T.text,
					textDecoration: inv.isVoid ? 'line-through' : 'none',
					fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', letterSpacing: '-0.01em',
				}}>
					{inv.isUnpaid ? inv.balanceFmt : inv.totalFmt}
				</Box>
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', minWidth: 0 }}>
				{inv.canCheckout && inv.appointmentId && onOpenAppointment && (
					<ButtonBase
						title="Opens the appointment — checkout, no-show and cancel all happen there"
						onClick={(e) => { e.stopPropagation(); onOpenAppointment(inv) }}
						sx={{ ...ghostBtn, height: 30, px: '11px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, color: T.primaryDeep, gap: '6px', '&:hover': { borderColor: T.primary, bgcolor: T.primarySoft } }}
					>
						<InvIcon name="calendar" size={13} sw={1.7} />Open appointment
					</ButtonBase>
				)}
				{(inv.isPaid || inv.isVoid) && onDownload && (
					<ButtonBase
						aria-label="Download PDF" title="Download PDF"
						disabled={downloadingId === inv.id}
						onClick={(e) => { e.stopPropagation(); onDownload(inv) }}
						sx={{
							width: 30, height: 30, flex: 'none', border: `1px solid ${T.border}`, borderRadius: '8px',
							bgcolor: T.field, color: T.muted, opacity: downloadingId === inv.id ? 0.5 : 1,
							'&:hover': { color: T.primaryDeep, borderColor: T.primary },
						}}
					>
						<InvIcon name="download" size={14} />
					</ButtonBase>
				)}
			</Box>
		</Box>
	)
}

function DesktopTable({ rows, tab, searching, totals, onOpen, onOpenAppointment, onDownload, downloadingId }) {
	const col4Label = searching ? 'Status' : { unpaid: 'Total', upcoming: 'Time · groomer', paid: 'Paid by', void: 'Voided' }[tab]
	const col5Label = searching ? 'Balance · total' : tab === 'unpaid' ? 'Balance due' : 'Total'
	const n = rows.length
	const footLeft = searching ? 'Searching across every status' : {
		unpaid: `${n} unpaid invoice${n === 1 ? '' : 's'} · owed longest first · payment is taken at checkout`,
		upcoming: `${n} upcoming appointment${n === 1 ? '' : 's'} · each completes itself at checkout`,
		paid: `${n} paid invoice${n === 1 ? '' : 's'} · each one is the receipt`,
		void: `${n} voided invoice${n === 1 ? '' : 's'} · records are kept, never deleted`,
	}[tab]
	const footRight = searching ? '' : {
		unpaid: `Total owed ${totals.balanceDueFmt}`,
		upcoming: `Booked ${totals.upcomingTotalFmt}`,
		paid: `Collected ${totals.paidTotalFmt}`,
		void: '',
	}[tab]
	return (
		<Box sx={{ ...card, overflow: 'hidden' }}>
			<Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: '14px', alignItems: 'center', p: '11px 18px', borderBottom: `1px solid ${T.divider}`, ...overline }}>
				<span>Client · pets</span>
				<span>Appointment</span>
				<span>Invoice</span>
				<Box component="span" sx={{ textAlign: !searching && tab === 'unpaid' ? 'right' : 'left' }}>{col4Label}</Box>
				<Box component="span" sx={{ textAlign: 'right' }}>{col5Label}</Box>
				<span></span>
			</Box>
			{rows.map((inv) => (
				<DesktopRow key={inv.id} inv={inv} searching={searching} onOpen={onOpen} onOpenAppointment={onOpenAppointment} onDownload={onDownload} downloadingId={downloadingId} />
			))}
			{rows.length === 0 && <EmptyRows searching={searching} />}
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', p: '10px 18px', borderTop: `1px solid ${T.divider}`, fontSize: '12px', fontWeight: 600, color: T.muted, fontFamily: T.font }}>
				<span>{footLeft}</span>
				<Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{footRight}</Box>
			</Box>
		</Box>
	)
}

/* ---------- mobile rows ---------- */

function MobileRow({ inv, searching, first, onOpen }) {
	const line3 = inv.isUpcoming
		? `${inv.dateFmt} · ${inv.time}${inv.staff ? ` · ${inv.staff}` : ''}`
		: `${inv.dateFmt} · ${inv.number}`
	const sub = inv.isUnpaid ? inv.daysLabel
		: inv.isUpcoming ? inv.rel
		: inv.isPaid ? (inv.payments[0]?.methodLabel || 'Paid')
		: 'Voided'
	const subColor = inv.isAging ? T.warnText : inv.isPaid ? T.okText : T.muted
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			{!first && <Box sx={{ height: '1px', bgcolor: T.rowDivider, mx: '14px' }} />}
			<Box onClick={() => onOpen(inv)} sx={{
				display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '12px', alignItems: 'center',
				p: '12px 14px', cursor: 'pointer', fontFamily: T.font,
				'&:active': { bgcolor: T.surface },
			}}>
				<Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
					<Box sx={{ fontSize: '15px', fontWeight: 750, color: inv.isVoid ? '#A9A3BE' : T.text, textDecoration: inv.isVoid ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.clientName}</Box>
					<Box sx={{ fontSize: '12.5px', fontWeight: 600, color: T.breed, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.petLine}</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', fontWeight: 600, color: T.muted, whiteSpace: 'nowrap', overflow: 'hidden', fontVariantNumeric: 'tabular-nums' }}>
						<span>{line3}</span>
						{searching && <StatusPill status={inv.status} small />}
					</Box>
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', textAlign: 'right', flex: 'none' }}>
					<Box sx={{
						fontSize: inv.isUnpaid ? '16px' : '15px', fontWeight: inv.isUnpaid ? 800 : 700,
						color: inv.isVoid ? '#A9A3BE' : inv.isUpcoming ? T.soft : T.text,
						textDecoration: inv.isVoid ? 'line-through' : 'none',
						fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', letterSpacing: '-0.01em',
					}}>
						{inv.isUnpaid ? inv.balanceFmt : inv.totalFmt}
					</Box>
					<Box sx={{ fontSize: '11.5px', fontWeight: inv.isAging ? 700 : 650, color: subColor, whiteSpace: 'nowrap' }}>{sub}</Box>
				</Box>
			</Box>
		</Box>
	)
}

/* ---------- the screen ---------- */

export default function InvoicesList({
	isMobile, loading, error, onRetry,
	views, byStatus, totals, tab, onTab, query, onQuery,
	rows, searching, onOpen, onOpenAppointment, onDownload, downloadingId,
}) {
	if (error) {
		return (
			<Box sx={{ p: isMobile ? '14px 16px 26px' : 0 }}>
				<LoadError onRetry={onRetry} />
			</Box>
		)
	}
	if (loading) {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', p: '80px 20px' }}>
				<CircularProgress size={34} sx={{ color: T.primary }} />
				<Box sx={{ fontSize: '12px', fontWeight: 600, color: T.muted, fontFamily: T.font }}>Loading your invoices…</Box>
			</Box>
		)
	}
	if (views.length === 0) {
		return (
			<Box sx={{ p: isMobile ? '14px 16px 26px' : 0 }}>
				<FirstRun isMobile={isMobile} />
			</Box>
		)
	}

	const caption = searching ? `${rows.length} result${rows.length === 1 ? '' : 's'} for “${query.trim()}”` : CAPTIONS[tab]
	const aside = {
		unpaid: `${totals.balanceDueFmt} owed`,
		upcoming: `${totals.upcomingTotalFmt} booked`,
		paid: `${totals.paidCount} receipt${totals.paidCount === 1 ? '' : 's'}`,
		void: `${totals.voidCount} voided`,
	}[tab]

	if (isMobile) {
		return (
			<Box sx={{ p: '14px 16px 26px', display: 'flex', flexDirection: 'column', gap: '12px', fontFamily: T.font }}>
				{/* the one number */}
				<Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', p: '14px 16px 15px', ...card, borderRadius: '16px' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
						<Box sx={overline}>Balance due</Box>
						<Box sx={{ fontSize: '30px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: T.text }}>{totals.balanceDueFmt}</Box>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', textAlign: 'right', flex: 'none', pb: '3px' }}>
						<Box sx={{ fontSize: '12.5px', fontWeight: 650, color: T.text, whiteSpace: 'nowrap' }}>
							{totals.unpaidCount === 0 ? 'Nothing owed' : `${totals.unpaidCount} unpaid invoice${totals.unpaidCount === 1 ? '' : 's'}`}
						</Box>
						<Box sx={{ fontSize: '12px', fontWeight: totals.oldestDays >= 7 ? 650 : 600, color: totals.oldestDays >= 7 ? T.warnText : T.muted, whiteSpace: 'nowrap' }}>
							{totals.unpaidCount === 0 ? 'All caught up' : `Oldest ${totals.oldestDays} day${totals.oldestDays === 1 ? '' : 's'}`}
						</Box>
					</Box>
				</Box>
				<SearchBox query={query} onQuery={onQuery} isMobile />
				{!searching && <Tabs tab={tab} onTab={onTab} totals={totals} isMobile />}
				<Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', px: '2px', mb: '-3px' }}>
					<Box sx={{ fontSize: '12px', fontWeight: 600, color: T.muted }}>{caption}</Box>
					{!searching && <Box sx={{ fontSize: '12px', fontWeight: 650, color: T.soft, fontVariantNumeric: 'tabular-nums' }}>{aside}</Box>}
				</Box>
				<Box sx={{ ...card, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
					{rows.map((inv, i) => (
						<MobileRow key={inv.id} inv={inv} searching={searching} first={i === 0} onOpen={onOpen} />
					))}
					{rows.length === 0 && <EmptyRows searching={searching} />}
				</Box>
			</Box>
		)
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: T.font }}>
			{/* page header */}
			<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box component="h1" sx={{ m: 0, fontSize: '23px', fontWeight: 800, letterSpacing: '-0.02em', color: T.text }}>Invoices</Box>
					<Box sx={{ mt: '3px', fontSize: '12.5px', fontWeight: 600, color: T.muted }}>Created from appointments, completed at checkout — nothing to set up.</Box>
				</Box>
				<SearchBox query={query} onQuery={onQuery} />
			</Box>
			{/* the one number */}
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', py: '2px' }}>
				<Box sx={overline}>Balance due</Box>
				<Box sx={{ fontSize: '36px', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: T.text }}>{totals.balanceDueFmt}</Box>
				<Box sx={{ fontSize: '12.5px', fontWeight: 600, color: T.muted }}>
					{totals.unpaidCount === 0
						? 'Nothing owed — all caught up'
						: `Across ${totals.unpaidCount} unpaid invoice${totals.unpaidCount === 1 ? '' : 's'} · the oldest is ${totals.oldestDays} day${totals.oldestDays === 1 ? '' : 's'} old`}
				</Box>
			</Box>
			{/* tabs / search caption */}
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
				{searching ? (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13.5px', fontWeight: 700, color: T.text }}>
						{caption}
						<ButtonBase onClick={() => onQuery('')} sx={{ color: T.primary, fontSize: '12.5px', fontWeight: 700, p: '5px 8px', borderRadius: '7px', fontFamily: T.font, '&:hover': { bgcolor: T.primarySoft } }}>Clear search</ButtonBase>
					</Box>
				) : (
					<>
						<Tabs tab={tab} onTab={onTab} totals={totals} />
						<Box sx={{ fontSize: '12.5px', fontWeight: 650, color: T.soft, fontVariantNumeric: 'tabular-nums' }}>{aside}</Box>
					</>
				)}
			</Box>
			<DesktopTable rows={rows} tab={tab} searching={searching} totals={totals} onOpen={onOpen} onOpenAppointment={onOpenAppointment} onDownload={onDownload} downloadingId={downloadingId} />
		</Box>
	)
}
